"use client";

/**
 * Root-level provider that mounts the embedded-host shell for the current
 * `AppSurface`.
 *
 * On the standalone web surface this is a no-op pass-through apart from
 * publishing the host value via `<HostContext>` so every page can call
 * `useHost()`.
 *
 * On any embedded surface (Office add-in, Procore panel, …) it resolves the
 * surface's shell from the registry (`components/embedded-hosts.tsx`) and
 * mounts it around the app. Each shell owns its surface-specific concerns
 * (Office.js + MSAL for the add-ins; the postMessage context handshake +
 * sign-in/connect gate for Procore). Adding a copilot means registering a
 * shell + metadata + (optionally) a host-context renderer — no change here.
 *
 * Mounted from the root layout so every page rendered inside the host gets the
 * same shell.
 */
import { c as _c } from "react/compiler-runtime";
import type { AppSurface } from "@workspace/services/domain";
import { type ReactNode, useEffect, useState } from "react";
import { MagnusAppProvider } from "@/components/magnus-app-provider";
import { getClientHost, isEmbeddedSurface } from "@/lib/host";
import { HostContextProvider } from "@/lib/host-context";
import { getEmbeddedHostShell } from "./embedded-hosts";
type HostProviderProps = {
  /**
   * Initial surface resolved server-side from the `magnus-host` cookie.
   * Used to pick a render strategy on first mount; a mismatch with the
   * client-side sessionStorage value (rare — only on the bootstrap page
   * before the cookie has been written) is reconciled when the
   * sessionStorage value lands.
   */
  host: AppSurface;
  children: ReactNode;
};
export function HostProvider(t0) {
  const $ = _c(14);
  const {
    host: serverHost,
    children
  } = t0;
  const [host, setHost] = useState(serverHost);
  let t1;
  let t2;
  if ($[0] !== serverHost) {
    t1 = () => {
      if (serverHost !== "standalone-web") {
        return;
      }
      const clientHost = getClientHost();
      if (clientHost !== "standalone-web" && isEmbeddedSurface(clientHost)) {
        setHost(clientHost);
      }
    };
    t2 = [serverHost];
    $[0] = serverHost;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== host) {
    t3 = getEmbeddedHostShell(host);
    $[3] = host;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const Shell = t3;
  let t4;
  if ($[5] !== children || $[6] !== host) {
    t4 = <MagnusAppProvider host={host}>{children}</MagnusAppProvider>;
    $[5] = children;
    $[6] = host;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const app = t4;
  let t5;
  if ($[8] !== Shell || $[9] !== app) {
    t5 = Shell ? <Shell>{app}</Shell> : app;
    $[8] = Shell;
    $[9] = app;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== host || $[12] !== t5) {
    t6 = <HostContextProvider value={host}>{t5}</HostContextProvider>;
    $[11] = host;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}